import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor: Add JWT token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor: Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - but don't redirect automatically
      // Some endpoints like /sessions/current return 401 when no session exists, which is normal
      // Let each component/store handle 401 errors appropriately

      // Only redirect if it's an authentication error on a critical endpoint
      const url = error.config?.url || ''

      // Don't redirect for endpoints that can legitimately return 401
      const nonCriticalEndpoints = ['/sessions/current', '/sessions/']
      const isNonCritical = nonCriticalEndpoints.some(endpoint => url.includes(endpoint))

      if (!isNonCritical) {
        // This is a real authentication failure
        console.warn('[Auth] Authentication failed, clearing token')
        localStorage.removeItem('auth_token')

        // Use dynamic import to avoid circular dependency
        if (window.location.pathname !== '/login') {
          import('@/router').then(({ default: router }) => {
            router.push({
              name: 'Login',
              query: { redirect: window.location.pathname }
            })
          })
        }
      }
    }
    return Promise.reject(error)
  }
)

export default apiClient
